import { Link } from "react-router-dom";
import { Gem, Layers, Compass, Sparkles, Target, Building2 } from "lucide-react";
import { MonoLabel, Em, Card } from "@/site/components/Atoms";

const ITEMS = [
  {
    n: "01",
    t: "Selected Advisory Environment",
    Icon: Gem,
    paragraphs: [
      "Forealco works with a focused advisory environment built around relevance, quality, discretion, and project fit. This may include accounting, tax, legal, certification, institutional, commercial, or sector-specific advisors depending on the opportunity.",
      "The role of the advisory environment is not only to provide opinions. It is to support the structure, reduce uncertainty, and help the route remain credible as it moves toward execution.",
    ],
  },
  {
    n: "02",
    t: "Erenown Ecosystem Support",
    Icon: Layers,
    paragraphs: [
      "Where relevant, Forealco may coordinate with specialized brands within the wider Erenown ecosystem, including Hubinall and Hangonin.",
      "Hubinall can support digital workflows, advisory-office tools, documentation logic, financial-data coordination, ERP-linked processes, and operational business infrastructure.",
      "Hangonin can support local presence, company setup support, address coordination, practical landing needs, marketplace onboarding, and settlement-related business support in selected markets.",
      "These brands are not presented as generic service add-ons. They are potential execution layers where the opportunity requires digital, operational, advisory-office, or local presence support.",
    ],
  },
  {
    n: "03",
    t: "Curated Coordination",
    Icon: Compass,
    paragraphs: [
      `Forealco shapes support around the needs of the opportunity, not around a generic provider list. The relevant question is not "Who do we know?" but "Which actors are required for this route to work?"`,
      "Coordination may include defining roles, sequencing support, clarifying responsibilities, preparing communication points, and identifying gaps before the opportunity moves forward.",
    ],
  },
  {
    n: "04",
    t: "Quality Over Quantity",
    Icon: Sparkles,
    paragraphs: [
      "Forealco values clarity, confidentiality, discretion, and execution discipline over broad, unfocused networks. Some opportunities require a small number of reliable actors rather than a large list of contacts.",
      "This is especially important in cross-border market entry, institutional opportunities, trade coordination, defense-related support, and public modernization contexts where trust, documentation, and reputational control matter.",
    ],
  },
  {
    n: "05",
    t: "Project-Specific Execution Logic",
    Icon: Target,
    paragraphs: [
      "Each opportunity requires its own structure, support model, and path toward realization. A digital product route, a trade coordination case, a Kosovo market entry plan, a UK-facing startup structure, an institutional project, or an energy-related opportunity will not be supported in the same way.",
      "Forealco evaluates what the opportunity requires before shaping the support environment. The aim is to avoid unnecessary complexity while ensuring that the essential layers are present.",
    ],
  },
  {
    n: "06",
    t: "Institutional and Commercial Pathways",
    Icon: Building2,
    paragraphs: [
      "Some opportunities require access to institutional environments, donor-backed programs, chambers, technology parks, advisory offices, banks, public-sector stakeholders, or sector-specific commercial channels.",
      "Forealco does not treat these pathways as simple introductions. They are assessed in relation to the opportunity's credibility, timing, documentation, partner readiness, and implementation logic.",
    ],
  },
];

export default function Network() {
  return (
    <>
      {/* HERO */}
      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 pt-16 lg:pt-20">
        <div className="fade-up">
          <MonoLabel>NETWORK</MonoLabel>
        </div>
        <h1 className="h-display fade-up delay-1 mt-6 text-[44px] sm:text-[58px] lg:text-[68px] max-w-4xl">
          A curated advisory and <Em>execution environment.</Em>
        </h1>
        <p className="fade-up delay-2 mt-7 max-w-2xl text-[15px] leading-[1.85] text-white/70">
          Forealco works through a curated support environment designed around quality, discretion,
          relevance, and execution discipline. The network is not presented as a broad provider
          list. It is shaped around the route, structure, and practical requirements of each
          opportunity.
        </p>
      </section>

      <NetworkPrinciple />

      {/* NETWORK ITEMS */}
      <section
        data-testid="network-items"
        className="max-w-[1500px] mx-auto px-6 lg:px-10 mt-28 grid grid-cols-1 lg:grid-cols-2 gap-3"
      >
        {ITEMS.map(({ n, t, Icon, paragraphs }) => (
          <Card
            key={n}
            data-testid={`network-item-${n}`}
            className="p-7 lg:p-8 group"
          >
            <div className="flex items-start gap-5">
              <div className="shrink-0 w-11 h-11 border border-[#9DB4FF]/40 flex items-center justify-center text-[#9DB4FF] group-hover:bg-[#9DB4FF]/10 transition-colors">
                <Icon size={18} strokeWidth={1.4} />
              </div>
              <div className="flex-1 min-w-0">
                <MonoLabel>{n}</MonoLabel>
                <div className="mt-3 font-display text-[24px] md:text-[26px] leading-[1.2]">
                  {t}
                </div>
                <div className="mt-4 space-y-3.5 text-[13.5px] leading-[1.85] text-white/70">
                  {paragraphs.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        ))}
      </section>

      {/* CLOSING */}
      <Closing />
    </>
  );
}

/* ───────────────── Network Principle ───────────────── */
function NetworkPrinciple() {
  return (
    <section
      data-testid="network-principle"
      className="max-w-[1500px] mx-auto px-6 lg:px-10 mt-28"
    >
      <div className="grid grid-cols-12 gap-10">
        <div className="col-span-12 lg:col-span-5">
          <MonoLabel>NETWORK PRINCIPLE</MonoLabel>
          <h2 className="h-display mt-5 text-[34px] md:text-[44px] leading-[1.08]">
            The value of a network is not the <Em>number of contacts it contains.</Em>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-7 lg:pl-6 space-y-5 text-[15px] leading-[1.95] text-white/72 max-w-3xl">
          <p>
            Forealco's network approach is based on relevance, not volume. A useful support
            environment is one where the right actors can be aligned around a specific opportunity,
            with clear roles, practical coordination, and a realistic path toward execution.
          </p>
          <p>
            A market entry case, a trade route, a digital infrastructure opportunity, an
            institutional modernization project, a defense-related support environment, or an
            advisory-led business structure will not need the same support model.
          </p>
          <p>
            For this reason, Forealco shapes the network around the opportunity. Where relevant,
            this may include advisory partners, accounting and legal support, local presence
            providers, technology partners, commercial contacts, institutional pathways, trade
            coordination actors, operational support, or specialized brands within the wider
            Erenown ecosystem, including Hubinall and Hangonin.
          </p>
          <p>
            The objective is not to introduce more parties. The objective is to align the right
            support layers so the opportunity can move with greater clarity, confidentiality, and
            execution discipline.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ───────────────── Closing ───────────────── */
function Closing() {
  return (
    <section
      data-testid="network-closing"
      className="max-w-[1500px] mx-auto px-6 lg:px-10 mt-28"
    >
      <div className="border border-white/10 bg-white/[0.025] p-10 md:p-14 relative overflow-hidden">
        <div
          className="absolute inset-x-0 top-0 h-px"
          style={{
            background:
              "linear-gradient(90deg, transparent, #9DB4FF 50%, transparent)",
            opacity: 0.5,
          }}
          aria-hidden="true"
        />
        <MonoLabel>NEXT STEP</MonoLabel>
        <h2 className="h-display mt-5 text-[30px] md:text-[42px] leading-[1.12] max-w-4xl">
          Shape the right support environment <Em>before moving forward.</Em>
        </h2>
        <div className="mt-6 max-w-2xl space-y-4 text-[15px] leading-[1.9] text-white/70">
          <p>
            A business opportunity can lose momentum when the wrong support structure is used.
            Forealco helps identify which advisory, local, digital, operational, commercial, or
            institutional layers are actually required for the opportunity to move toward
            execution.
          </p>
          <p>
            The purpose is not to build a large network around every project. The purpose is to
            build the right support environment for the route.
          </p>
        </div>
        <div className="mt-8 flex items-center gap-3 flex-wrap">
          <Link
            to="/explore-opportunities"
            data-testid="network-cta-primary"
            className="btn-primary"
          >
            Explore Opportunities →
          </Link>
          <Link to="/about" data-testid="network-cta-secondary" className="btn-secondary">
            About Forealco
          </Link>
        </div>
      </div>
    </section>
  );
}
